<template>
  <div class="vehicle-selector bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/30 dark:to-pink-900/30 border-2 border-purple-200 dark:border-purple-700 rounded-lg p-3" :key="language">
    <div class="selector-header flex items-center justify-between mb-2">
      <div class="flex items-center gap-2">
        <span class="text-xl">🚗</span>
        <h4 class="text-sm font-bold text-purple-900 dark:text-purple-100">
          {{ t('selectedVehicle') }}
        </h4>
      </div>
      <button
        v-if="!isExpanded"
        @click="isExpanded = true"
        class="text-xs font-medium text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300 bg-white/50 dark:bg-gray-800/50 px-3 py-1 rounded-full transition-all"
      >
        🔄 {{ t('change') }}
      </button>
    </div>

    <!-- Selected Vehicle Display (Collapsed) -->
    <div v-if="!isExpanded && selectedVehicle" class="selected-vehicle bg-white/70 dark:bg-gray-800/70 rounded-lg p-2 border border-purple-200 dark:border-purple-700">
      <div class="flex items-center gap-3">
        <div 
          class="vehicle-icon-small w-10 h-10 rounded-full flex items-center justify-center text-xl shadow-md"
          :style="{ backgroundColor: selectedVehicle.color + '30', color: selectedVehicle.color }"
        >
          {{ selectedVehicle.icon || '🚗' }}
        </div>
        <div class="flex-1">
          <div class="text-sm font-semibold text-gray-900 dark:text-gray-100">
            {{ selectedVehicle.name }}
          </div>
          <div class="text-xs text-gray-600 dark:text-gray-400 flex items-center gap-2">
            <span class="bg-purple-100 dark:bg-purple-900/40 px-2 py-0.5 rounded-full">
              {{ selectedVehicle.fuelType === 'electric' ? '⚡' : '⛽' }} {{ selectedVehicle.fuelConsumptionCombined }} {{ selectedVehicle.fuelType === 'electric' ? 'kWh' : 'L' }}/100km
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- No Vehicle Selected -->
    <div v-if="!isExpanded && !selectedVehicle" class="no-vehicle bg-white/70 dark:bg-gray-800/70 rounded-lg p-3 border-2 border-dashed border-purple-300 dark:border-purple-600 text-center">
      <div class="text-sm text-gray-600 dark:text-gray-400 mb-2">
        {{ t('noVehicleSelected') }}
      </div>
      <button
        @click="$emit('add-vehicle')"
        class="text-xs font-medium text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300 bg-purple-100 dark:bg-purple-900/40 px-3 py-1 rounded-full transition-all"
      >
        ➕ {{ t('addVehicle') }}
      </button>
    </div>

    <!-- Vehicle List (Expanded) -->
    <div v-if="isExpanded" class="vehicle-list space-y-2 max-h-60 overflow-y-auto">
      <div 
        v-for="vehicle in vehicles"
        :key="vehicle.id"
        class="vehicle-item cursor-pointer bg-white/70 dark:bg-gray-800/70 rounded-lg p-2 border-2 transition-all hover:shadow-md"
        :class="{ 
          'border-purple-500 dark:border-purple-400 bg-purple-100/50 dark:bg-purple-900/30': vehicle.id === modelValue,
          'border-purple-200 dark:border-purple-700 hover:border-purple-400': vehicle.id !== modelValue
        }"
        @click="selectVehicle(vehicle.id)"
      >
        <div class="flex items-center gap-3">
          <div 
            class="vehicle-icon w-10 h-10 rounded-full flex items-center justify-center text-xl shadow-md"
            :style="{ backgroundColor: vehicle.color + '30', color: vehicle.color }"
          >
            {{ vehicle.icon || '🚗' }}
          </div>
          <div class="flex-1">
            <div class="flex items-center gap-2">
              <span class="text-sm font-semibold text-gray-900 dark:text-gray-100">
                {{ vehicle.name }}
              </span>
              <span v-if="vehicle.isDefault" class="badge-default text-xs bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 px-2 py-0.5 rounded-full">
                ✓ {{ t('default') }}
              </span>
            </div>
            <div class="text-xs text-gray-600 dark:text-gray-400 flex items-center gap-1">
              <span class="bg-purple-100 dark:bg-purple-900/40 px-2 py-0.5 rounded-full">
                {{ vehicle.fuelType === 'electric' ? '⚡' : '⛽' }} {{ vehicle.fuelConsumptionCombined }} {{ vehicle.fuelType === 'electric' ? 'kWh' : 'L' }}/100km
              </span>
            </div>
            <div v-if="vehicle.make || vehicle.model" class="text-xs text-gray-500 dark:text-gray-500">
              {{ [vehicle.make, vehicle.model, vehicle.year].filter(Boolean).join(' ') }}
            </div>
          </div>
          <div v-if="vehicle.id === modelValue" class="check-icon">
            <ion-icon :icon="checkmarkCircle" class="text-purple-600 dark:text-purple-400 text-2xl"></ion-icon>
          </div>
        </div>
      </div>

      <!-- Add New Vehicle Option -->
      <button
        @click="$emit('add-vehicle')"
        class="add-vehicle-button w-full bg-white/70 dark:bg-gray-800/70 hover:bg-purple-100 dark:hover:bg-purple-900/30 rounded-lg p-3 border-2 border-dashed border-purple-300 dark:border-purple-600 transition-all flex items-center justify-center gap-2 font-medium text-purple-700 dark:text-purple-300"
      >
        <ion-icon :icon="addCircleOutline" class="text-xl"></ion-icon>
        <span>{{ t('addNewVehicle') }}</span>
      </button>

      <!-- Close Button -->
      <button
        @click="isExpanded = false"
        class="close-button w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-lg p-2 font-semibold transition-all shadow-md mt-2"
      >
        ✓ {{ t('done') }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { IonIcon } from '@ionic/vue'
import { checkmarkCircle, addCircleOutline } from 'ionicons/icons'
import type { Vehicle } from '@/types'
import { useI18n } from '@/i18n'
import { useSettingsStore } from '@/stores/settings'
import { storeToRefs } from 'pinia'

interface Props {
  vehicles: Vehicle[]
  modelValue?: string | null  // Selected vehicle ID
}

const props = defineProps<Props>()
const emit = defineEmits<{
  'update:modelValue': [vehicleId: string | null]
  'add-vehicle': []
}>()

const { t } = useI18n()
const settingsStore = useSettingsStore()
const { language } = storeToRefs(settingsStore)

const isExpanded = ref(false)

const selectedVehicle = computed(() => {
  if (!props.modelValue) return null
  return props.vehicles.find(v => v.id === props.modelValue)
})

function selectVehicle(vehicleId: string) {
  emit('update:modelValue', vehicleId)
  isExpanded.value = false
}
</script>
